<template>
    <CompassModal
        v-model:visible="showModal"
        :title="title"
        :size="CompassEnums.ModalSize.SMALL"
        :primaryAction="primaryAction"
        :secondaryActions="secondaryActions"
        :coloredIconProps="{
            size: CompassEnums.ColoredIconSize.LARGE,
            color: CompassEnums.ColoredIconColorType.TEAL,
            icon: 'lock-fill',
        }"
        class="dialog-finalize-document"
        @closed="emit('close')"
    >
        <template v-if="isMetadataLoading">
            <CompassSkeleton />
            <CompassSkeleton :size="CompassEnums.SkeletonSize.EXTRA_LARGE" />
        </template>
        <template v-else>
            <ComplianceAlert v-if="forceComplianceAlert" />
            <CompassAlert
                v-if="paymentsToLink.length > 0"
                :type="CompassEnums.AlertType.WARNING"
                :description="$t.__('document.DialogValidateDocument.alert_payments_to_link')"
                :showClose="false"
            />
            <slot name="before-content" />
            <p
                class="dialog-finalize-document__attention typographies-paragraph-medium-regular"
                v-html="warningText"
            />
            <p
                class="dialog-finalize-document__list-description typographies-paragraph-medium-regular"
            >
                {{ $t.__('document.DialogValidateDocument.finalize.list_description') }}
            </p>
            <ul class="dialog-finalize-document__list">
                <li
                    v-for="(item, index) in finalListInfo"
                    :key="index"
                    class="typographies-paragraph-medium-regular"
                    v-html="item"
                />
            </ul>
            <CompassFormItem
                :label="$t.__('document.DialogValidateDocument.finalize.datepicker_label')"
            >
                <CompassDatePicker
                    v-model="tempDate"
                    :disabled="isDatePickerLocked"
                    :calendarOptions="calendarOptions"
                />
            </CompassFormItem>
        </template>
    </CompassModal>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue';
import type { Translator } from 'types/injects/translator.type';
import type { PushToast } from 'types/injects/pushToast.type';
import { captureException } from '@sentry/vue';
import type { Payment } from 'types/document/documentFormStore.types';
import ComplianceAlert from 'vcomponents/common/document/ComplianceAlert.vue';

enum DialogType {
    FINALIZE = 'finalize',
    VALIDATE = 'validate',
}
interface ValidateDocumentMetadata {
    date: string;
    lastValidationDate: string | null;
    paymentsToLink: Payment[];
}
interface Props {
    validate: (date: string) => Promise<string>;
    type?: DialogType;
    fetchMetadata?: (() => Promise<ValidateDocumentMetadata>) | null;
    forceComplianceAlert?: boolean;
    canUpdateDate?: boolean;
}

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const props = withDefaults(defineProps<Props>(), {
    type: DialogType.FINALIZE,
    fetchMetadata: null,
    forceComplianceAlert: false,
    canUpdateDate: true,
});

const emit = defineEmits<{ close: [] }>();

const showModal = ref<boolean>(true);
const isSubmitting = ref<boolean>(false);
const isMetadataLoading = ref<boolean>(false);
const currentDate = moment().format('YYYY-MM-DD');
const tempDate = ref<string>(currentDate);
const lastValidationDate = ref<string | null>(null);
const paymentsToLink = ref<Payment[]>([]);

const finalListInfo = computed<string[]>(() => {
    return [
        $t.__(`document.DialogValidateDocument.finalize.final_list_info_1`),
        $t.__(`document.DialogValidateDocument.finalize.final_list_info_2`),
        $t.__(`document.DialogValidateDocument.finalize.final_list_info_3`),
    ];
});
const warningText = computed<string>(() => {
    return props.canUpdateDate
        ? $t.__('document.DialogValidateDocument.finalize.warning_text')
        : $t.__('document.DialogValidateDocument.validate.warning_text_can_not_update_date');
});
const title = computed<string>(() =>
    props.type === DialogType.FINALIZE
        ? $t.__('document.DialogValidateDocument.finalize.title')
        : $t.__('document.DialogValidateDocument.validate.title'),
);
const primaryAction = computed<{
    label: string;
    action: () => void;
    loading: boolean;
    dataBot: string | null;
}>(() => ({
    label:
        props.type === DialogType.FINALIZE
            ? $t.__('document.DialogValidateDocument.finalize.finalize_btn')
            : $t.__('common.actions.confirm'),
    action: handleValidate,
    loading: isSubmitting.value,
    dataBot: 'popup-finalize-document__button--validate',
}));
const secondaryActions = computed<{ label: string; action: () => void; disabled: boolean }[]>(
    () => [
        {
            label: $t.__('common.actions.cancel'),
            action: (): void => emit('close'),
            disabled: isSubmitting.value,
        },
    ],
);

const calendarOptions = computed(() => {
    if (props.canUpdateDate) {
        return {};
    }
    return {
        maxDate: currentDate,
        minDate: lastValidationDate.value,
    };
});

const isDatePickerLocked = computed<boolean>(
    () => !props.canUpdateDate && lastValidationDate.value === null,
);

const handleValidate = async (): Promise<void> => {
    try {
        isSubmitting.value = true;
        await props.validate(tempDate.value);
    } catch (error: unknown) {
        const typedError = error as { message?: string };
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            text: typedError?.message ?? '',
        });
    } finally {
        isSubmitting.value = false;
    }
};

onMounted(async () => {
    if (!props.fetchMetadata) {
        return;
    }
    try {
        isMetadataLoading.value = true;
        const metadata = await props.fetchMetadata();
        if (metadata) {
            lastValidationDate.value = metadata.lastValidationDate;
            paymentsToLink.value = metadata.paymentsToLink as Payment[];

            const minDate = metadata.lastValidationDate ?? currentDate;
            tempDate.value = moment(metadata.date).isBefore(minDate) ? minDate : metadata.date;
        }
    } catch (e) {
        captureException(e);
        emit('close');
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            text: $t.__('settings.sellsymodule.invoicing.conditions.toast_error_data_fetch'),
        });
    } finally {
        isMetadataLoading.value = false;
    }
});
</script>

<style lang="scss" scoped>
.dialog-finalize-document {
    &__attention {
        margin: 0 0 var(--spacing-3);
    }

    &__list-description {
        margin: 0 0 var(--spacing-0);
    }

    &__list {
        list-style: disc;
        margin: 0 0 var(--spacing-3);
        padding-left: var(--spacing-4);
    }

    .compass-datepicker {
        width: 150px;
    }

    .compass-skeleton:first-child {
        width: 59px;
        margin-bottom: var(--spacing-1);
    }
}
</style>
